import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  structuredData?: object;
}

export function SEO({
  title = "Block Pattern Generator | AI-Powered WordPress Block Builder",
  description = "Create stunning WordPress block patterns in seconds with AI. No coding required. Built for designers, developers, and content creators.",
  keywords = [
    "wordpress",
    "blocks",
    "patterns",
    "AI",
    "generator",
    "gutenberg",
  ],
  image = "https://voltblocks.com/wp-content/uploads/2024/12/Volt-Blocks-Favicon.png",
  url = "https://patterns.voltblocks.com",
  structuredData,
}: SEOProps) {
  const siteTitle = "Block Pattern Generator";
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />

      {/* Open Graph */}
      <meta property="og:site_name" content="Block Pattern Generator" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@blockpatterngenerator" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      {/* Theme Colors */}
      <meta name="theme-color" content="#0A0F1C" />
      <meta name="msapplication-TileColor" content="#0A0F1C" />

      {/* Icons */}
      <link
        rel="icon"
        type="image/svg+xml"
        href="https://voltblocks.com/wp-content/uploads/2024/12/Volt-Blocks-Favicon.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="manifest" href="/site.webmanifest" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
